import type { PropsWithChildren } from 'react';
import type React from 'react';
import { useMemo } from 'react';

import type { CustomThemeType, ThemeContextType } from '@/_shared';
import { CustomThemeContext, defaultTheme, mergeDeep, useThemeMode } from '@/_shared';

import type { DivProps, ThemeMode } from '../types';

/**
 * AtpLib is a React Component that provides theme configurations and functionality.
 * It uses the CustomThemeContext to manage the themes and the dark mode status.
 *
 * @component
 * @example
 * return (
 *   <AtpLib theme={myTheme} dark={true} usePreferences={true}>
 *     <MyComponent />
 *   </AtpLib>
 * )
 *
 * @param {object} props - The properties that define the AtpLib Component
 * @param {React.ReactNode} props.children - The child components that AtpLib will wrap
 * @param {object} [props.theme] - An optional theme object that defines the styles used throughout the library. If not provided, a default theme is used.
 * @param {boolean} [props.dark] - An optional boolean flag that indicates if the dark mode should be the default theme.
 * @param {boolean} [props.usePreferences=false] - An optional boolean flag that allows the change of theme, reading user's preferences and saving them. Defaults to false.
 *
 * @returns {React.Element} The AtpLib Component with child components
 */

type Props = DivProps & {
  children: React.ReactNode;
  /**
   * Defines the styles used throughout the library
   */
  theme?: CustomThemeType;
  /**
   * Defines dark mode as the default theme
   */
  preferredMode?: ThemeMode;
  /**
   * Allows the change of theme, reading user's preferences and saving them
   */
  usePreferences?: boolean;
};

export function CustomTheme({ children, theme: customTheme, preferredMode, usePreferences }: PropsWithChildren<Props>) {
  const [mode, , toggleMode] = useThemeMode({ preferredMode, usePreferences });
  const mergedTheme: CustomThemeType = useMemo(() => mergeDeep(defaultTheme, customTheme), [customTheme]);

  const value: ThemeContextType = useMemo(
    () => ({
      theme: mergedTheme,
      mode,
      toggleMode,
    }),
    [mode, toggleMode, mergedTheme]
  );

  return <CustomThemeContext value={value}>{children}</CustomThemeContext>;
}
